import { useState, useEffect, useCallback } from 'react'
import type {
  CreateExternalCrRequest,
  CustodyAction,
  CustodyDocument,
  ExternalCrOutput,
  LinkageCheckResult,
  ListCustodyActionsParams,
  ReceiveEvidenceRequest,
  StoreEvidenceRequest,
  RelocateEvidenceRequest,
  ReturnEvidenceRequest,
  DestroyEvidenceRequest,
} from '../types/custody-actions'
import {
  checkCollectionLinkage,
  createExternalCollectionRequest,
  listCustodyActions,
  getCustodyDocument,
  performReceive,
  performStore,
  performRelocate,
  performReturn,
  performDestroy,
} from '../services/custody-actions'

interface UseCustodyActionsResult {
  actions: CustodyAction[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useCustodyActions(
  caseId: string | null,
  params?: ListCustodyActionsParams
): UseCustodyActionsResult {
  const [actions, setActions] = useState<CustodyAction[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetchData` on every render, which
  // in turn re-fires the `useEffect([fetchData])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const actionType = params?.actionType
  const evidenceNumberId = params?.evidenceNumberId
  const page = params?.page
  const perPage = params?.perPage

  const fetchData = useCallback(async () => {
    if (!caseId) {
      setActions([])
      setTotal(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const result = await listCustodyActions(caseId, params)
      setActions(result.items)
      setTotal(result.total)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch custody actions')
      setActions([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, actionType, evidenceNumberId, page, perPage])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  return { actions, total, loading, error, refetch: fetchData }
}

type ActionFn<TInput> = (caseId: string, input: TInput) => Promise<CustodyAction>

interface ActionHandlerConfig<TInput> {
  apiFn: ActionFn<TInput>
  setState: { setPerforming: (v: boolean) => void; setError: (v: string | null) => void }
}

function makeActionHandler<TInput>(
  config: ActionHandlerConfig<TInput>,
  errorMsg: string
): (caseId: string, input: TInput) => Promise<CustodyAction | null> {
  return async (caseId: string, input: TInput): Promise<CustodyAction | null> => {
    config.setState.setPerforming(true)
    config.setState.setError(null)
    try {
      return await config.apiFn(caseId, input)
    } catch (error_) {
      config.setState.setError(error_ instanceof Error ? error_.message : errorMsg)
      return null
    } finally {
      config.setState.setPerforming(false)
    }
  }
}

interface UsePerformCustodyActionResult {
  performing: boolean
  error: string | null
  checking: boolean
  linkageResult: LinkageCheckResult | null
  creatingCr: boolean
  checkLinkage: (caseId: string, ids: string[]) => Promise<LinkageCheckResult | null>
  createExternalCr: (
    caseId: string,
    input: CreateExternalCrRequest
  ) => Promise<ExternalCrOutput | null>
  clearLinkageResult: () => void
  performReceive: (caseId: string, input: ReceiveEvidenceRequest) => Promise<CustodyAction | null>
  performStore: (caseId: string, input: StoreEvidenceRequest) => Promise<CustodyAction | null>
  performRelocate: (caseId: string, input: RelocateEvidenceRequest) => Promise<CustodyAction | null>
  performReturn: (caseId: string, input: ReturnEvidenceRequest) => Promise<CustodyAction | null>
  performDestroy: (caseId: string, input: DestroyEvidenceRequest) => Promise<CustodyAction | null>
  clearError: () => void
}

export function usePerformCustodyAction(): UsePerformCustodyActionResult {
  const [performing, setPerforming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [linkageResult, setLinkageResult] = useState<LinkageCheckResult | null>(null)
  const [creatingCr, setCreatingCr] = useState(false)

  const clearError = useCallback(() => {
    setError(null)
  }, [])
  const clearLinkageResult = useCallback(() => {
    setLinkageResult(null)
  }, [])
  const setState = { setPerforming, setError }

  const checkLinkageFn = useCallback(
    async (caseId: string, ids: string[]): Promise<LinkageCheckResult | null> => {
      setChecking(true)
      setError(null)
      try {
        const result = await checkCollectionLinkage(caseId, { evidenceNumberIds: ids })
        setLinkageResult(result)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check collection linkage')
        return null
      } finally {
        setChecking(false)
      }
    },
    []
  )

  const createExternalCrFn = useCallback(
    async (caseId: string, input: CreateExternalCrRequest): Promise<ExternalCrOutput | null> => {
      setCreatingCr(true)
      setError(null)
      try {
        const result = await createExternalCollectionRequest(caseId, input)
        return result
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to create external collection request'
        )
        return null
      } finally {
        setCreatingCr(false)
      }
    },
    []
  )

  return {
    performing,
    error,
    checking,
    linkageResult,
    creatingCr,
    checkLinkage: checkLinkageFn,
    createExternalCr: createExternalCrFn,
    clearLinkageResult,
    performReceive: makeActionHandler(
      { apiFn: performReceive, setState },
      'Failed to record receipt'
    ),
    performStore: makeActionHandler({ apiFn: performStore, setState }, 'Failed to record storage'),
    performRelocate: makeActionHandler(
      { apiFn: performRelocate, setState },
      'Failed to record relocation'
    ),
    performReturn: makeActionHandler({ apiFn: performReturn, setState }, 'Failed to record return'),
    performDestroy: makeActionHandler(
      { apiFn: performDestroy, setState },
      'Failed to record destruction'
    ),
    clearError,
  }
}

interface UseCustodyDocumentResult {
  document: CustodyDocument | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useCustodyDocument(
  caseId: string | null,
  actionId: string | null
): UseCustodyDocumentResult {
  const [document, setDocument] = useState<CustodyDocument | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!caseId || !actionId) {
      setDocument(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await getCustodyDocument(caseId, actionId)
      setDocument(result)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch custody document')
      setDocument(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, actionId])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  return { document, loading, error, refetch: fetchData }
}
